import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// react-bootstrap
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';
import Image from 'react-bootstrap/Image';
import Nav from 'react-bootstrap/Nav';
import Stack from 'react-bootstrap/Stack';

// project-imports
import SimpleBarScroll from 'components/third-party/SimpleBar';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { useAuth } from 'context/AuthContext';
import { useThemeMode } from 'context/ThemeModeContext';
import { listNotifications, markAllNotificationsRead, markNotificationRead, clearNotifications } from 'api/notifications';
import { getActiveScans } from 'api/features';

// assets
import Img1 from 'assets/images/user/avatar-1.png';
import Img2 from 'assets/images/user/avatar-2.png';
import Img3 from 'assets/images/user/avatar-3.png';
import Img4 from 'assets/images/user/avatar-4.png';
import Img5 from 'assets/images/user/avatar-5.png';

const avatarPool = [Img1, Img2, Img3, Img4, Img5];

// =============================|| MAIN LAYOUT - HEADER ||============================== //

export default function Header() {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;
  const { user, logout } = useAuth();
  const { mode: themeMode, toggleMode } = useThemeMode();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/auth/login', { replace: true });
  };

  const displayName =
    user?.first_name ||
    user?.username ||
    user?.email ||
    'User';

  const displayEmail = user?.email || '';
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifs, setLoadingNotifs] = useState(false);

  // Global active-scans feed (visible from any page)
  const [activeScans, setActiveScans] = useState({ count: 0, running: 0, pending: 0, scans: [] });

  const loadActiveScans = async () => {
    try {
      const data = await getActiveScans();
      setActiveScans(data || { count: 0, running: 0, pending: 0, scans: [] });
    } catch {
      /* keep last known state on transient errors */
    }
  };

  useEffect(() => {
    loadActiveScans();
    // Poll faster (5s) so the header reflects scan activity in near real-time.
    const interval = setInterval(loadActiveScans, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    setLoadingNotifs(true);
    try {
      const data = await listNotifications();
      setNotifications(data.results || data || []);
    } finally {
      setLoadingNotifs(false);
    }
  };

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
    loadNotifications();
  };

  const handleClearAll = async () => {
    await clearNotifications();
    loadNotifications();
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markNotificationRead(notification.id);
      await loadNotifications();
    }
  };

  const formatGroupDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const sameDay = (a, b) => a.toDateString() === b.toDateString();
    if (sameDay(d, today)) return 'Today';
    if (sameDay(d, yesterday)) return 'Yesterday';
    return d.toLocaleDateString();
  };

  return (
    <header className="pc-header">
      <div className="header-wrapper">
        <div className="me-auto pc-mob-drp">
          <Nav className="list-unstyled">
            <Nav.Item className="pc-h-item pc-sidebar-collapse">
              <Nav.Link
                as={Link}
                to="#"
                className="pc-head-link ms-0"
                id="sidebar-hide"
                onClick={() => {
                  handlerDrawerOpen(!drawerOpen);
                }}
              >
                <i className="ph ph-list" />
              </Nav.Link>
            </Nav.Item>

            <Nav.Item className="pc-h-item pc-sidebar-popup">
              <Nav.Link
                as={Link}
                to="#"
                className="pc-head-link ms-0"
                id="mobile-collapse"
                onClick={() => handlerDrawerOpen(!drawerOpen)}
              >
                <i className="ph ph-list" />
              </Nav.Link>
            </Nav.Item>

            <Dropdown className="pc-h-item dropdown">
              <Dropdown.Toggle
                variant="link"
                className="pc-head-link arrow-none m-0 trig-drp-search"
                id="dropdown-search"
              >
                <i className="ph ph-magnifying-glass" />
              </Dropdown.Toggle>
              <Dropdown.Menu className="pc-h-dropdown drp-search">
                <Form className="px-3 py-2">
                  <Form.Control
                    type="search"
                    placeholder="Search here. . ."
                    className="border-0 shadow-none"
                  />
                </Form>
              </Dropdown.Menu>
            </Dropdown>
          </Nav>
        </div>
        <div className="ms-auto">
          <Nav className="list-unstyled">
            {/* Global active-scans indicator */}
            <Dropdown className="pc-h-item" align="end">
              <Dropdown.Toggle
                className="pc-head-link me-0 arrow-none"
                variant="link"
                id="active-scans-dropdown"
                title={activeScans.count > 0 ? `${activeScans.count} active scan(s)` : 'No active scans'}
              >
                <i
                  className="ph ph-radar"
                  style={activeScans.running > 0 ? { color: '#f59e0b' } : undefined}
                />
                {activeScans.count > 0 && (
                  <span
                    className="badge pc-h-badge"
                    style={{
                      backgroundColor: activeScans.running > 0 ? '#f59e0b' : '#94a3b8',
                      animation: activeScans.running > 0 ? 'fuzzPulseBadge 1.4s ease-in-out infinite' : 'none'
                    }}
                  >
                    {activeScans.count}
                  </span>
                )}
              </Dropdown.Toggle>

              <Dropdown.Menu className="pc-h-dropdown" style={{ minWidth: 340, maxWidth: 'min(94vw, 420px)', padding: 0 }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--fz-card-divider)' }}>
                  <h5 className="m-0" style={{ fontSize: '0.95rem', fontWeight: 700 }}>Active Scans</h5>
                  <small style={{ color: 'var(--fz-text-secondary)' }}>
                    {activeScans.running} running · {activeScans.pending} queued
                  </small>
                </div>
                <SimpleBarScroll style={{ maxHeight: 'calc(100vh - 220px)' }}>
                  <div style={{ padding: 8 }}>
                    {activeScans.scans.length === 0 ? (
                      <div style={{ padding: '24px 8px', textAlign: 'center', color: 'var(--fz-text-muted)' }}>
                        <i className="ph ph-check-circle" style={{ fontSize: '1.4rem', display: 'block', marginBottom: 6, color: '#22c55e' }} />
                        <span style={{ fontSize: '0.82rem' }}>No scans running</span>
                      </div>
                    ) : (
                      activeScans.scans.map((scan) => {
                        const pct = Number(scan.progress || 0);
                        const isPending = scan.status === 'pending';
                        const barColor = isPending ? '#94a3b8' : '#f59e0b';
                        return (
                          <Link
                            key={scan.id}
                            to={scan.route}
                            className="active-scan-item"
                            style={{
                              display: 'block',
                              padding: '10px 12px',
                              borderRadius: 10,
                              marginBottom: 6,
                              border: '1px solid var(--fz-border-subtle)',
                              textDecoration: 'none'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--fz-text-primary)' }}>
                                {String(scan.scan_type || '').replace(/_/g, ' ').toUpperCase()}
                              </span>
                              <span style={{ fontSize: '0.7rem', fontWeight: 800, color: barColor }}>{pct}%</span>
                            </div>
                            <div style={{ fontSize: '0.7rem', color: 'var(--fz-text-muted)', marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {scan.target_name} · {scan.progress_message || (isPending ? 'Queued, waiting for worker…' : 'Running…')}
                            </div>
                            <div style={{ height: 4, borderRadius: 10, background: 'rgba(148,163,184,0.18)', overflow: 'hidden' }}>
                              <div style={{
                                height: '100%',
                                width: pct > 0 ? `${pct}%` : '30%',
                                background: barColor,
                                borderRadius: 10,
                                transition: 'width 0.5s ease',
                                animation: pct === 0 ? 'fuzzIndeterminate 1.4s ease-in-out infinite' : 'none'
                              }} />
                            </div>
                          </Link>
                        );
                      })
                    )}
                  </div>
                </SimpleBarScroll>
              </Dropdown.Menu>
            </Dropdown>

            <Nav.Item className="pc-h-item">
              <Nav.Link
                as="button"
                type="button"
                className="pc-head-link"
                onClick={toggleMode}
                aria-label={`Switch to ${themeMode === 'dark' ? 'light' : 'dark'} mode`}
                title={`Switch to ${themeMode === 'dark' ? 'light' : 'dark'} mode`}
              >
                <i className={`ph ${themeMode === 'dark' ? 'ph-sun' : 'ph-moon'}`} />
              </Nav.Link>
            </Nav.Item>
            <Dropdown className="pc-h-item" align="end">
              <Dropdown.Toggle
                className="pc-head-link me-0 arrow-none"
                variant="link"
                id="notification-dropdown"
              >
                <i className="ph ph-bell" />
                {unreadCount > 0 && (
                  <span className="badge bg-success pc-h-badge">{unreadCount}</span>
                )}
              </Dropdown.Toggle>

              <Dropdown.Menu className="dropdown-notification pc-h-dropdown fuzz-notification-menu">
                <Dropdown.Header className="fuzz-notification-head">
                  <div>
                    <h5 className="m-0">Notifications</h5>
                    <small>{unreadCount} unread</small>
                  </div>
                  <Stack direction="horizontal" gap={1}>
                    <Button variant="outline-success" size="sm" onClick={handleMarkAllRead}>
                      Mark all
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={handleClearAll}>
                      Clear
                    </Button>
                  </Stack>
                </Dropdown.Header>
                <SimpleBarScroll style={{ maxHeight: 'calc(100vh - 215px)' }}>
                  <div className="dropdown-body text-wrap position-relative fuzz-notification-body">
                    {loadingNotifs ? (
                      <div className="fuzz-notification-empty">
                        <i className="ph ph-spinner-gap ph-spin" />
                        <span>Loading notifications...</span>
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="fuzz-notification-empty">
                        <i className="ph ph-bell-slash" />
                        <span>No notifications</span>
                      </div>
                    ) : (
                      notifications.map((notification, index) => {
                        const group = formatGroupDate(notification.created_at);
                        const prevGroup = index > 0 ? formatGroupDate(notifications[index - 1].created_at) : null;
                        return (
                          <React.Fragment key={notification.id}>
                            {index === 0 || prevGroup !== group ? (
                              <p className="fuzz-notification-group">{group}</p>
                            ) : null}
                            <div
                              className={`fuzz-notification-item ${notification.is_read ? '' : 'is-unread'}`}
                              style={{ animationDelay: `${Math.min(index * 35, 280)}ms` }}
                              onClick={async () => {
                                await handleNotificationClick(notification);
                              }}
                            >
                              <div className="fuzz-notification-avatar">
                                <Image src={avatarPool[index % avatarPool.length]} alt="Notification avatar" />
                              </div>
                              <div className="fuzz-notification-content">
                                <div className="fuzz-notification-top">
                                  <h6>{notification.title}</h6>
                                  <span>
                                    {new Date(notification.created_at).toLocaleTimeString()}
                                  </span>
                                </div>
                                <p>{notification.message}</p>
                                {notification.link && (
                                  <Link
                                    to={notification.link}
                                    className="fuzz-notification-link"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Open details <i className="ph ph-arrow-up-right" />
                                  </Link>
                                )}
                              </div>
                              {!notification.is_read && <span className="fuzz-notification-dot" />}
                            </div>
                          </React.Fragment>
                        );
                      })
                    )}
                  </div>
                </SimpleBarScroll>
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown className="pc-h-item" align="end">
              <Dropdown.Toggle
                className="pc-head-link arrow-none me-0"
                variant="link"
                id="user-profile-dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                <i className="ph ph-user-circle" />
              </Dropdown.Toggle>

              <Dropdown.Menu className="dropdown-user-profile pc-h-dropdown p-0 overflow-hidden" style={{ background: 'var(--fz-surface-1)', border: '1px solid var(--fz-card-border)' }}>
                <Dropdown.Header style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.12), rgba(6,182,212,0.06))', borderBottom: '1px solid var(--fz-card-divider)' }}>
                  <Stack direction="horizontal" gap={3} className="my-2">
                    <div className="flex-shrink-0">
                      <Image
                        src={Img2}
                        alt="user-avatar"
                        className="user-avatar wid-35"
                        roundedCircle
                        style={{ border: '2px solid rgba(34,197,94,0.4)' }}
                      />
                    </div>
                    <Stack gap={1}>
                      <h6 className="mb-0" style={{ color: 'var(--fz-text-primary)' }}>{displayName}</h6>
                      {displayEmail && (
                        <span style={{ color: 'var(--fz-text-secondary)', fontSize: '0.8rem' }}>
                          {displayEmail}
                        </span>
                      )}
                    </Stack>
                  </Stack>
                </Dropdown.Header>

                <div className="dropdown-body">
                  <div
                    className="profile-notification-scroll position-relative"
                    style={{ maxHeight: 'calc(100vh - 225px)' }}
                  >
                    <Dropdown.Item as={Link} to="/settings" className="justify-content-start">
                      <i className="ph ph-gear me-2" />
                      Account Settings
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/settings/third-party-keys" className="justify-content-start">
                      <i className="ph ph-key me-2" />
                      Third-party Keys
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/" className="justify-content-start">
                      <i className="ph ph-chart-line-up me-2" />
                      Dashboard
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <div className="d-grid px-2 pb-1">
                      <Button variant="outline-danger" size="sm" onClick={handleLogout}>
                        <i className="ph ph-sign-out align-middle me-2" />
                        Logout
                      </Button>
                    </div>
                  </div>
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </Nav>
        </div>
      </div>
    </header>
  );
}
